import React from 'react';
import { CountryCode } from '@dayetopia/library/types';
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { PageSlice, SliceFAQsDetails, SliceFAQsItem } from '@contracts';
import useAccordionProcessedFaqs from '@utils/useAccordionProcessedFaqs';
import useProcessedFaqs from '@utils/useProcessedFaqs';
import Accordion from './design/Accordion';
import { TextVariant, Typography } from './design/Typography';
import LinkTranslated from './LinkTranslated';

interface IProps {
  slice: PageSlice;
  title?: string;
  moreLink?: string;
  country?: CountryCode;
}

const FAQs: React.FC<IProps> = ({ slice, title, moreLink }) => {
  const { t } = useTranslation();
  const faqItems = (slice.items ?? []) as SliceFAQsItem[];
  const faqDetails = (slice.primary || {}) as SliceFAQsDetails;
  const backgroundColor = faqDetails.background_color ?? '';
  const textColor = faqDetails.text_color ?? '';
  const processedFaqs = useProcessedFaqs(faqItems);
  const accordionData = useAccordionProcessedFaqs(processedFaqs);

  const renderSchemaMarkup = () => {
    const schemaData = processedFaqs
      .map((processedFaq) => {
        const questionText = processedFaq?.questionText;
        const answerText = processedFaq?.answerTextString;

        if (!questionText) return null;

        return {
          '@type': 'Question',
          name: questionText,
          acceptedAnswer: {
            '@type': 'Answer',
            text: answerText
          }
        };
      })
      .filter(Boolean);

    const schemaString = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: schemaData
    });

    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schemaString }} />;
  };

  return (
    <Container $background={backgroundColor} $textColor={textColor}>
      {renderSchemaMarkup()}
      <Inner>
        <Top>
          <TextWrap>
            <Typography $variant={TextVariant.Heading3} $mobileVariant={TextVariant.Heading6} as="h4">
              {t(title || 'components.here-to-answer')}
            </Typography>
            <Typography $variant={TextVariant.BodyRegularLG}>
              <Trans
                i18nKey="components.faqs-read-all"
                components={{
                  a: <LinkTranslated to={moreLink} targetBlank onClick={(e) => e.stopPropagation()} />
                }}
              />
            </Typography>
          </TextWrap>
        </Top>
        <AccordionWrap>
          <Accordion
            items={accordionData}
            textColor={textColor}
            itemPadding="16px 20px"
            itemPaddingMobile="15px 0"
            contentPadding="0 20px"
            contentPaddingMobile="0"
            mobileTopDivider
            bottomDivider
            mobileBottomDivider
          />
        </AccordionWrap>
      </Inner>
    </Container>
  );
};

export default FAQs;

interface ContainerProps {
  $background?: string;
  $textColor?: string;
}

const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: ${(props) => (props.$background ? props.$background : 'transparent')};
  color: ${(props) => (props.$textColor ? props.$textColor : Colors.forestGreen)};
  width: 100%;
  padding: ${(props) => (props.$background ? '96px 0' : '0')};

  @media only screen and (max-width: 1024px) {
    padding: ${(props) => (props.$background ? '76px 30px' : '0 30px')};
  }

  @media only screen and (max-width: 600px) {
    padding: ${(props) => (props.$background ? '56px 16px' : '0 16px')};
  }
`;

const Inner = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  justify-content: space-between;
  align-items: flex-start;
  gap: 30px;

  @media (max-width: 1260px) {
    max-width: 980px;
    padding: 0 30px;
  }

  @media (max-width: 1024px) {
    flex-direction: column;
    padding: 0;
  }
`;

const Top = styled.div`
  display: flex;
  width: 530px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;

  @media (max-width: 1024px) {
    width: 100%;
    flex-direction: column;
    align-items: center;
    padding-bottom: 20px;
  }
`;

const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  color: inherit;

  a {
    text-decoration: underline;
    color: ${Colors.navyBlue};
  }

  @media (max-width: 1024px) {
    align-items: center;

    h4,
    p {
      text-align: center;
    }
  }

  @media (max-width: 450px) {
    h4 {
      max-width: 300px;
    }
  }
`;

const AccordionWrap = styled.div`
  margin-top: -16px;
  display: flex;
  width: 600px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;

  @media (max-width: 1024px) {
    width: 100%;
  }
`;
